import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

// Animated SVG rebuild of the Figma "future you" graph (node 11149:113628):
// a 5-gridline chart with a beige "NOW" dot bottom-left, a smooth S-curve
// rising up to a yellow "With ZOE" dot top-right, dashed vertical guides at
// each end, and "NOW" / "YOUR HEALTH" axis labels.
//
// Animation cadence mirrors the existing WeightCalculationScreen chart so the
// two screens feel of a piece: gridlines + start dot are visible immediately,
// the curve draws over ~1.5s after a brief beat, then the end dot + pill +
// dashed guide fade in once the line completes.
//
// All geometry constants below are eyeballed to match the Figma design.

const VIEW_WIDTH = 420
const VIEW_HEIGHT = 300
const PADDING_TOP = 36
const PADDING_BOTTOM = 48
const PADDING_LEFT = 36
const PLOT_WIDTH = VIEW_WIDTH - PADDING_LEFT * 2
const PLOT_HEIGHT = VIEW_HEIGHT - PADDING_TOP - PADDING_BOTTOM

const PATH_DELAY_MS = 400
const PATH_DRAW_MS = 1500
const END_ELEMENTS_DELAY_MS = PATH_DELAY_MS + PATH_DRAW_MS

const Container = styled.div`
  width: 100%;
  background-color: var(--colour-surface-cream, #f6f1e7);
  border-radius: var(--radius-12);
  padding: var(--grid-16) var(--grid-8) var(--grid-8) var(--grid-8);
  box-sizing: border-box;

  svg {
    display: block;
    width: 100%;
    height: auto;
  }

  @keyframes future-you-path-draw {
    from {
      stroke-dashoffset: 1;
    }
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes future-you-end-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const startPoint = { x: PADDING_LEFT, y: PADDING_TOP + PLOT_HEIGHT * 0.85 }
const endPoint = { x: PADDING_LEFT + PLOT_WIDTH, y: PADDING_TOP + PLOT_HEIGHT * 0.05 }

// 3-step staircase curve from start (low) to end (high). Each step is a
// mini S-curve that rises then flattens, eyeballed to approximate the Figma
// design while keeping the code simple.
const STEPS = 3
const buildCurvePath = (): string => {
  const totalDx = endPoint.x - startPoint.x
  const totalDy = endPoint.y - startPoint.y // negative (going up)
  const stepDx = totalDx / STEPS
  const stepDy = totalDy / STEPS

  let d = `M ${startPoint.x} ${startPoint.y}`
  for (let i = 0; i < STEPS; i++) {
    const x0 = startPoint.x + stepDx * i
    const y0 = startPoint.y + stepDy * i
    const x1 = x0 + stepDx
    const y1 = y0 + stepDy
    // Control points: first holds y flat at current level, second holds y
    // flat at the next level — producing a smooth rise then plateau.
    const cp1x = x0 + stepDx * 0.55
    const cp1y = y0
    const cp2x = x0 + stepDx * 0.45
    const cp2y = y1
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x1} ${y1}`
  }
  return d
}

const PILL_LABEL = 'With ZOE'
const PILL_HEIGHT = 28
const PILL_FONT_SIZE = 13
const PILL_GAP_ABOVE = 18
// Stroke width as a proportion of viewBox height, matching WeightCalculationScreen (VIEW_H * 0.024).
const PATH_STROKE_WIDTH = VIEW_HEIGHT * 0.024

export const FutureYouGraph: React.FC = () => {
  const [pathVisible, setPathVisible] = useState(false)
  const [endVisible, setEndVisible] = useState(false)

  useEffect(() => {
    const pathTimer = window.setTimeout(() => setPathVisible(true), PATH_DELAY_MS)
    const endTimer = window.setTimeout(() => setEndVisible(true), END_ELEMENTS_DELAY_MS)
    return () => {
      window.clearTimeout(pathTimer)
      window.clearTimeout(endTimer)
    }
  }, [])

  const linePath = buildCurvePath()
  const baselineY = PADDING_TOP + PLOT_HEIGHT
  const yellow = 'var(--colour-surface-yellow-bold, #fac800)'
  const beige = 'var(--colour-surface-beige, #d6c6a8)'
  const gridColor = 'var(--colour-border-default, #e5e0d8)'
  const subtle = 'var(--colour-text-secondary, #757677)'

  // [i18n] This character-width heuristic only works for Latin/ASCII glyphs.
  // For CJK, Arabic, or other wide/complex scripts, replace with a proper
  // text measurement approach (e.g. canvas measureText or a lookup table).
  const pillW = Math.max(64, PILL_LABEL.length * 8 + 20)
  // Centre above the dot, but clamp so it never overflows the viewBox.
  const pillX = Math.min(endPoint.x - pillW / 2, VIEW_WIDTH - pillW)
  const pillY = endPoint.y - PILL_HEIGHT - PILL_GAP_ABOVE

  return (
    <Container>
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-label="Health improvement graph showing progress with ZOE"
        role="img"
      >
        {/* Gradient from beige (start) to yellow (end) matching Figma */}
        <defs>
          <linearGradient id="future-you-path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={beige} />
            <stop offset="100%" stopColor={yellow} />
          </linearGradient>
        </defs>

        {/* Horizontal gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = PADDING_TOP + PLOT_HEIGHT * (1 - fraction)
          return (
            <line
              key={fraction}
              x1={PADDING_LEFT}
              y1={y}
              x2={PADDING_LEFT + PLOT_WIDTH}
              y2={y}
              stroke={gridColor}
              strokeWidth="1"
            />
          )
        })}

        {/* Start dashed vertical guide — visible from the start */}
        <line
          x1={startPoint.x}
          y1={startPoint.y}
          x2={startPoint.x}
          y2={baselineY}
          stroke={beige}
          strokeWidth="1.5"
          strokeDasharray="3 4"
        />

        {/* Animated S-curve */}
        {pathVisible && (
          <path
            d={linePath}
            pathLength={1}
            fill="none"
            stroke="url(#future-you-path-gradient)"
            strokeWidth={PATH_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: 1,
              strokeDashoffset: 1,
              animation: `future-you-path-draw ${PATH_DRAW_MS}ms cubic-bezier(0.2, 0.55, 0.35, 0.95) forwards`,
            }}
          />
        )}

        {/* Start dot — beige, visible from the start */}
        <circle cx={startPoint.x} cy={startPoint.y} r={7} fill={beige} />

        {/* NOW axis label */}
        <text x={startPoint.x} y={baselineY + 22} textAnchor="middle" fill={subtle} fontSize="12">
          NOW
        </text>

        {/* End elements — fade in once the curve has finished drawing */}
        {/* End dot rendered outside the fade group so it covers the round
            linecap seamlessly — same colour, no flicker. */}
        {endVisible && <circle cx={endPoint.x} cy={endPoint.y} r={7} fill={yellow} />}

        {endVisible && (
          <g style={{ animation: 'future-you-end-fade 0.3s ease-out forwards', opacity: 0 }}>
            <line
              x1={endPoint.x}
              y1={endPoint.y}
              x2={endPoint.x}
              y2={baselineY}
              stroke={yellow}
              strokeWidth="1.5"
              strokeDasharray="3 4"
            />

            {/* Yellow "With ZOE" pill above the end dot */}
            <rect
              x={pillX}
              y={pillY}
              width={pillW}
              height={PILL_HEIGHT}
              rx={PILL_HEIGHT / 2}
              ry={PILL_HEIGHT / 2}
              fill={yellow}
            />
            <text
              x={pillX + pillW / 2}
              y={pillY + PILL_HEIGHT / 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="var(--colour-text-strong, #191a1d)"
              fontSize={PILL_FONT_SIZE}
              fontWeight="600"
            >
              {PILL_LABEL}
            </text>

            <text x={endPoint.x} y={baselineY + 22} textAnchor="end" fill={subtle} fontSize="12">
              YOUR HEALTH
            </text>
          </g>
        )}
      </svg>
    </Container>
  )
}
